import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

const ModernPieChart = ({ 
  data = [], 
  centerTitle = "Total", 
  centerValue, 
  colors = ["#3B82F6", "#8BC78F"]
}) => {
  // Handle case where data is undefined or empty
  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-400 text-sm">No data available</p>
      </div>
    );
  }

  // Transform data to Recharts format
  const chartData = data.map((item, index) => ({
    name: item.title || item.name,
    value: item.total_employees || item.value || 0,
    color: colors[index] || colors[0]
  }));

  const totalValue = centerValue !== undefined && centerValue !== null 
    ? centerValue 
    : chartData.reduce((sum, item) => sum + item.value, 0);

  // Custom label for center text
  const renderCenterLabel = () => {
    return (
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        <tspan
          x="50%"
          dy="-10"
          style={{
            fontSize: "16px",
            fontWeight: "600",
            fill: "#4B5563",
          }}
        >
          {centerTitle}
        </tspan>
        <tspan
          x="50%"
          dy="30"
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            fill: "#1E3A8A",
          }}
        >
          {totalValue}
        </tspan>
      </text>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length && payload[0]) {
      const data = payload[0];
      const value = data.value || 0;
      const percentage = totalValue > 0 ? ((value / totalValue) * 100).toFixed(1) : '0.0';
      return (
        <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 text-sm mb-1">{data.name || 'N/A'}</p>
          <p className="text-gray-600 text-sm">
            <span className="font-bold text-[#1E3A8A]">{value}</span>
            <span className="text-gray-400 ml-2">({percentage}%)</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const renderLegend = (props) => {
    const { payload } = props;
    
    // Handle case where payload is undefined or empty
    if (!payload || payload.length === 0) {
      return null;
    }
    
    return (
      <ul className="flex items-center justify-center gap-6 mt-4">
        {payload.map((entry, index) => {
          const percentage = ((entry.payload.value / totalValue) * 100).toFixed(1);
          return (
            <li key={`legend-${index}`} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-gray-700 font-medium">
                {entry.value.replace(' Organizations', '').replace(' Projects', '')}
                <span className="text-gray-500 ml-1">({percentage}%)</span>
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <ResponsiveContainer width="100%" height="85%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius="60%"
            outerRadius="80%"
            paddingAngle={2}
            dataKey="value"
            strokeWidth={0}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color}
                style={{
                  filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {renderCenterLabel()}
        </PieChart>
      </ResponsiveContainer>
      <div className="w-full px-4">
        <Legend content={renderLegend} />
      </div>
    </div>
  );
};

export default ModernPieChart;
